import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Building2, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Heart, 
  Calendar,
  Shield,
  FileText,
  Users,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  ArrowLeft
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicClientReferralPage() {
  const { agencyCode } = useParams();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    // Client Information
    client_first_name: "",
    client_last_name: "",
    client_date_of_birth: "",
    client_phone: "",
    client_email: "",
    client_address: "",
    client_city: "",
    client_state: "",
    client_zip_code: "",
    // Insurance Info
    insurance_type: "",
    insurance_id: "",
    // Physician / Doctor
    physician_name: "",
    physician_phone: "",
    physician_fax: "",
    physician_address: "",
    physician_specialty: "",
    physician_npi: "",
    // Service Needs
    services_needed: [],
    diagnosis: "",
    special_needs: "",
    // Referral Source
    referrer_name: "",
    referrer_phone: "",
    referrer_email: "",
    referrer_organization: "",
    referrer_relationship: "",
    // Additional
    urgency: "normal",
    preferred_contact_method: "phone",
    additional_notes: ""
  });

  useEffect(() => {
    fetchAgencyInfo();
  }, [agencyCode]);

  const fetchAgencyInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/agency/${agencyCode}`);
      setAgency(response.data);
      setError(null);
    } catch (err) {
      setError("Agency not found. Please check the link and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services_needed: prev.services_needed.includes(service)
        ? prev.services_needed.filter(s => s !== service)
        : [...prev.services_needed, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.client_first_name || !formData.client_last_name || !formData.referrer_name || !formData.referrer_phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/agency/${agencyCode}/client-referral`, formData);
      setSubmitted(true);
      toast.success("Referral submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit referral. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Agency Not Found</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Go to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Referral Submitted!</h1>
          <p className="text-slate-600 mb-6">
            Thank you for your referral to {agency?.name}. Our intake team will review the information 
            and contact you within 1-2 business days.
          </p>
          <p className="text-sm text-slate-500">
            You can close this window now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{agency?.name}</h1>
              <p className="text-slate-500">Client Referral Form</p>
            </div>
          </div>
          <div className="text-sm text-slate-600 flex flex-wrap gap-4">
            {agency?.address && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {agency.city}, {agency.state}
              </span>
            )}
            {agency?.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {agency.phone}
              </span>
            )}
            {agency?.email && (
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {agency.email}
              </span>
            )}
          </div>
        </div>

        {/* Referral Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 space-y-6">
          <div className="border-b border-slate-200 pb-4">
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Heart className="w-5 h-5 text-emerald-600" />
              Client Referral
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Fields marked with * are required
            </p>
          </div>

          {/* Client Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              Client Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client First Name *</Label>
                <Input
                  value={formData.client_first_name}
                  onChange={(e) => handleChange("client_first_name", e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Client Last Name *</Label>
                <Input
                  value={formData.client_last_name}
                  onChange={(e) => handleChange("client_last_name", e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.client_date_of_birth}
                  onChange={(e) => handleChange("client_date_of_birth", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Client Phone</Label>
                <Input
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => handleChange("client_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Client Email</Label>
              <Input
                type="email"
                value={formData.client_email}
                onChange={(e) => handleChange("client_email", e.target.value)}
                placeholder="client@example.com"
                data-testid="referral-client-email"
              />
            </div>

            <div className="space-y-2">
              <Label>Client Address</Label>
              <Input
                value={formData.client_address}
                onChange={(e) => handleChange("client_address", e.target.value)}
                placeholder="123 Main Street"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>City</Label>
                <Input
                  value={formData.client_city}
                  onChange={(e) => handleChange("client_city", e.target.value)}
                  placeholder="Milwaukee"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={formData.client_state}
                  onChange={(e) => handleChange("client_state", e.target.value)}
                  placeholder="WI"
                />
              </div>
              <div className="space-y-2">
                <Label>ZIP Code</Label>
                <Input
                  value={formData.client_zip_code}
                  onChange={(e) => handleChange("client_zip_code", e.target.value)}
                  placeholder="53201"
                />
              </div>
            </div>
          </div>

          {/* Insurance Information */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Shield className="w-4 h-4 text-slate-400" />
              Insurance Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Insurance Type</Label>
                <Select 
                  value={formData.insurance_type} 
                  onValueChange={(v) => handleChange("insurance_type", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select insurance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medicaid">Medicaid</SelectItem>
                    <SelectItem value="medicare">Medicare</SelectItem>
                    <SelectItem value="private">Private Insurance</SelectItem>
                    <SelectItem value="self_pay">Self Pay</SelectItem>
                    <SelectItem value="va">VA Benefits</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Insurance ID / Member Number</Label>
                <Input
                  value={formData.insurance_id}
                  onChange={(e) => handleChange("insurance_id", e.target.value)}
                  placeholder="Insurance ID"
                />
              </div>
            </div>
          </div>

          {/* Physician / Doctor */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Heart className="w-4 h-4 text-slate-400" />
              Primary Care Doctor / Physician (optional)
            </h3>
            <p className="text-xs text-slate-500 -mt-2">
              Providing your doctor&apos;s info helps us coordinate care faster.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Doctor&apos;s Name</Label>
                <Input
                  value={formData.physician_name}
                  onChange={(e) => handleChange("physician_name", e.target.value)}
                  placeholder="Dr. Jane Smith"
                  data-testid="referral-physician-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Specialty</Label>
                <Input
                  value={formData.physician_specialty}
                  onChange={(e) => handleChange("physician_specialty", e.target.value)}
                  placeholder="e.g., Primary Care, Cardiology"
                  data-testid="referral-physician-specialty"
                />
              </div>
              <div className="space-y-2">
                <Label>Doctor&apos;s Phone</Label>
                <Input
                  type="tel"
                  value={formData.physician_phone}
                  onChange={(e) => handleChange("physician_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  data-testid="referral-physician-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Doctor&apos;s Fax</Label>
                <Input
                  type="tel"
                  value={formData.physician_fax}
                  onChange={(e) => handleChange("physician_fax", e.target.value)}
                  placeholder="(555) 123-4568"
                  data-testid="referral-physician-fax"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Doctor&apos;s Office Address</Label>
                <Input
                  value={formData.physician_address}
                  onChange={(e) => handleChange("physician_address", e.target.value)}
                  placeholder="123 Medical Plaza, City, State ZIP"
                  data-testid="referral-physician-address"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>NPI Number (optional)</Label>
                <Input
                  value={formData.physician_npi}
                  onChange={(e) => handleChange("physician_npi", e.target.value)}
                  placeholder="10-digit National Provider Identifier"
                  data-testid="referral-physician-npi"
                />
              </div>
            </div>
          </div>

          {/* Services Needed */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Heart className="w-4 h-4 text-slate-400" />
              Services Needed (Check all that apply)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                "Personal Care",
                "Skilled Nursing",
                "Companion Care",
                "Respite Care",
                "Meal Preparation",
                "Medication Reminders",
                "Transportation",
                "Light Housekeeping",
                "Physical Therapy",
                "Occupational Therapy",
                "Speech Therapy",
                "Hospice Care"
              ].map((service) => (
                <div key={service} className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.services_needed.includes(service)}
                    onCheckedChange={() => handleServiceToggle(service)}
                    id={service}
                  />
                  <label htmlFor={service} className="text-sm text-slate-700 cursor-pointer">
                    {service}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Medical Information */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" />
              Medical Information
            </h3>
            <div className="space-y-2">
              <Label>Primary Diagnosis / Reason for Care</Label>
              <Textarea
                value={formData.diagnosis}
                onChange={(e) => handleChange("diagnosis", e.target.value)}
                placeholder="e.g., Diabetes, CHF, Post-surgery recovery, etc."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Special Needs or Considerations</Label>
              <Textarea
                value={formData.special_needs}
                onChange={(e) => handleChange("special_needs", e.target.value)}
                placeholder="e.g., Wheelchair access, memory care, dietary restrictions, etc."
                rows={2}
              />
            </div>
          </div>

          {/* Referral Source */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              Referral Source / Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Name *</Label>
                <Input
                  value={formData.referrer_name}
                  onChange={(e) => handleChange("referrer_name", e.target.value)}
                  placeholder="Your full name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Your Phone *</Label>
                <Input
                  type="tel"
                  value={formData.referrer_phone}
                  onChange={(e) => handleChange("referrer_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Your Email</Label>
                <Input
                  type="email"
                  value={formData.referrer_email}
                  onChange={(e) => handleChange("referrer_email", e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Organization</Label>
                <Input
                  value={formData.referrer_organization}
                  onChange={(e) => handleChange("referrer_organization", e.target.value)}
                  placeholder="Hospital, clinic, or organization name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Relationship to Client</Label>
              <Select 
                value={formData.referrer_relationship} 
                onValueChange={(v) => handleChange("referrer_relationship", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Self</SelectItem>
                  <SelectItem value="family">Family Member</SelectItem>
                  <SelectItem value="hospital">Hospital/Discharge Planner</SelectItem>
                  <SelectItem value="physician">Physician/Medical Office</SelectItem>
                  <SelectItem value="social_worker">Social Worker</SelectItem>
                  <SelectItem value="case_manager">Case Manager</SelectItem>
                  <SelectItem value="friend">Friend</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Urgency & Contact Preference */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-slate-400" />
              Urgency & Contact Preference
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Urgency Level</Label>
                <Select 
                  value={formData.urgency} 
                  onValueChange={(v) => handleChange("urgency", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select urgency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent - Immediate need</SelectItem>
                    <SelectItem value="normal">Normal - Within 1-2 weeks</SelectItem>
                    <SelectItem value="low">Low - Planning ahead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preferred Contact Method</Label>
                <Select 
                  value={formData.preferred_contact_method} 
                  onValueChange={(v) => handleChange("preferred_contact_method", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="text">Text Message</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="space-y-4 pt-4 border-t">
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={formData.additional_notes}
                onChange={(e) => handleChange("additional_notes", e.target.value)}
                placeholder="Any other information that would help us serve this client better"
                rows={3}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-6 border-t">
            <Button 
              type="submit" 
              className="w-full py-6 text-lg gap-2 bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  Submit Referral
                </>
              )}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-4">
              By submitting this referral, you authorize {agency?.name} to contact you and the client regarding care services.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
